import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhoneCall, CalendarClock, Heart } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";

const Consultation = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [antidepressants, setAntidepressants] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload = buildTrackingPayload(
      "consultation-form",
      "Consultation Request",
    );
    payload.formData = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      calendar_notes: message
        ? `${message}${antidepressants ? ` | Antidepressant history: ${antidepressants}` : ""}`
        : antidepressants
          ? `Antidepressant history: ${antidepressants}`
          : "",
    };
    payload.formLabels = {
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      calendar_notes: "Message",
    };

    postTrackingEvent(payload, {
      customFields: {
        yWO5ZTXyq5IpOcNsYrIz: {
          value: antidepressants,
          label: "Have you tried antidepressants",
        },
      },
    });

    trackLead("consultation-form", "Consultation Request");
    trackLeadConversion("consultation-form", "Consultation Request");
    navigate("/thank-you-consultation");
  };

  return (
    <section
      id="consultation"
      className="relative overflow-hidden bg-gradient-to-b from-primary-deep via-primary to-primary-deep py-16 text-primary-foreground lg:py-28"
    >
      <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-accent/8 blur-3xl" />
      <div className="container-px relative mx-auto grid max-w-7xl items-center gap-10 sm:gap-12 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
            <Heart className="h-3.5 w-3.5" />
            You're Not Alone in This
          </span>
          <h2 className="mt-5 font-headings text-2xl font-bold leading-tight sm:text-3xl lg:text-[2.75rem]">
            Let's find your path forward — together
          </h2>
          <p className="mt-4 text-base leading-relaxed text-primary-foreground/75 sm:mt-5 sm:text-lg">
            Reaching out is the hardest part, and you've already started. Tell
            us a little about yourself and our compassionate care team will
            reach out within one business day to answer your questions, verify
            your insurance, and help you take the next step — at no cost and no
            obligation.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-primary-foreground/60">
            Two pathways to care: self-pay offers the fastest access (most
            patients start within days, with appointments available seven days a
            week), or we accept Blue Cross Blue Shield in select states and the
            BCBS Federal Employee Program in select states. We'll help you find
            the right path for your situation.
          </p>

          <ul className="mt-8 space-y-3.5">
            {[
              {
                icon: Heart,
                text: "Warm, judgment-free consultation — no pressure, just answers",
              },
              {
                icon: CalendarClock,
                text: "Flexible scheduling: Zoom or in-person, including weekends",
              },
              {
                icon: PhoneCall,
                text: "Same-day or next-day appointments often available",
              },
            ].map((item) => (
              <li key={item.text} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15">
                  <item.icon className="h-4 w-4 text-accent" />
                </div>
                <span className="text-primary-foreground/90">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-accent/20 bg-card p-5 text-card-foreground shadow-2xl sm:p-7 lg:p-9">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <h3 className="font-headings text-xl font-bold text-primary">
                Request your free consultation
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Takes 60 seconds. We'll handle the rest.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  required
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  required
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="jane@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                required
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="history">Have you tried antidepressants?</Label>
              <Select
                value={antidepressants}
                onValueChange={setAntidepressants}
              >
                <SelectTrigger id="history">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    No, I haven't tried medication
                  </SelectItem>
                  <SelectItem value="one">Yes, 1 medication</SelectItem>
                  <SelectItem value="two-plus">
                    Yes, 2 or more medications
                  </SelectItem>
                  <SelectItem value="unsure">I'm not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">
                Anything you'd like us to know? (optional)
              </Label>
              <Textarea
                id="message"
                rows={3}
                placeholder="Share any questions or details about your situation..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
            >
              <Heart className="h-4 w-4 mr-2" />
              Get My Free Consultation
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Your information is private and protected. We never share your
              details.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Consultation;
